import './Shell.scss';
import React, { useContext } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import isEmpty from 'lodash.isempty';
import cloneDeep from 'lodash.clonedeep';

import Device from '@/v1/components/application/Device';
import Loading from '@/v1/components/atoms/Loading';
import query from '@/v1/state/query';
import { ApplicationContext, ApplicationProvider, StorageProvider } from '@/v1/state/providers';
import Analytics from '@/v1/components/application/Analytics';
import BurgerMenuOverlay from '@/v1/components/layouts/BurgerMenuOverlay';
import RegisterInterestOverlay from '@/v1/components/layouts/RegisterInterestOverlay';
import DisclaimerOverlay from '@/v1/components/layouts/DisclaimerOverlay';
import CookieNoticeOverlay from '@/v1/components/layouts/CookieNoticeOverlay';
import { StateProvider } from '@/v1/state/store';
import FullscreenOverlay from '@/v1/components/atoms/FullscreenOverlay';
import FullscreenOverlayScroll from '@/v1/components/atoms/FullscreenOverlayScroll';
import PlatformController from '@/v1/components/controllers/PlatformController';

const Home = React.lazy(() => import(/* webpackChunkName: "home" */ '@/v1/routes/Home'));
const Menu = React.lazy(() => import(/* webpackChunkName: "menu" */ '@/v1/routes/Menu'));
const Serenely = React.lazy(() => import(/* webpackChunkName: "serenely" */ '@/v1/routes/moments/Serenely'));
const Effortlessly = React.lazy(() => import(/* webpackChunkName: "effortlessly" */ '@/v1/routes/moments/Effortlessly'));
const Powerfully = React.lazy(() => import(/* webpackChunkName: "powerfully" */ '@/v1/routes/moments/Powerfully'));
const Smartly = React.lazy(() => import(/* webpackChunkName: "smartly" */ '@/v1/routes/moments/Smartly'));

const ShellComponent = ({ data }) => {
    PlatformController();

    if (data.device) {
        return (
            <Router basename={window.HELIOS.aemBasePath}>
                <Analytics adobeTaggingObj={window.HELIOS.adobe.tagging}>
                    <StateProvider>
                        <Device data={data.device}>
                            <FullscreenOverlay />
                            <FullscreenOverlayScroll data={data.device.ios} />
                            <div className="Shell">
                                <React.Suspense fallback={<Loading />}>
                                    <Switch>
                                        <Route exact path="/" component={Home} />
                                        <Route path="/menu.html" component={Menu} />
                                        <Route path="/serenely.html" component={Serenely} />
                                        <Route path="/effortlessly.html" component={Effortlessly} />
                                        <Route path="/powerfully.html" component={Powerfully} />
                                        <Route path="/smartly.html" component={Smartly} />
                                    </Switch>
                                </React.Suspense>
                            </div>
                            <BurgerMenuOverlay />
                            <RegisterInterestOverlay />
                            <DisclaimerOverlay />
                            <CookieNoticeOverlay />
                        </Device>
                    </StateProvider>
                </Analytics>
            </Router>
        );
    }
    return <Loading />;
};

const moveInArray = (arr, from, to) => {
    // Make sure a valid array is provided
    if (Object.prototype.toString.call(arr) !== '[object Array]') {
        throw new Error('Please provide a valid array');
    }

    // Delete the item from it's current position
    const item = arr.splice(from, 1);

    // Make sure there's an item to move
    if (!item.length) {
        throw new Error(`There is no item in the array at index ${from}`);
    }

    // Move the item to its new position
    arr.splice(to, 0, item[0]);
};

const ShellRenderer = () => {
    const [data, setter] = useContext(ApplicationContext);

    // updating v1 data to match data in prod
    const modifyData = (originalData) => {
        if (!isEmpty(originalData)) {
            const originalMenuItems = originalData.header.menu.items;
            const smartlyIndex = originalMenuItems.findIndex(({ route }) => route === 'smartly.html');
            const smartlyOverViewHomeIndex = originalMenuItems
                .find(({ route }) => route === 'smartly.html')
                ?.submenu.findIndex(({ name }) => name === 'overview-home');
            const smartlyOverViewInCarIndex = originalMenuItems
                .find(({ route }) => route === 'smartly.html')
                ?.submenu.findIndex(({ name }) => name === 'overview-incar');
            const serenelyIndex = originalMenuItems.findIndex(({ route }) => route === 'serenely.html');

            const dataClone = cloneDeep(originalData);
            const menuItemsClone = dataClone?.header?.menu?.items;
            const smartlySubMenuItems = menuItemsClone.find(({ route }) => route === 'smartly.html')?.submenu;

            moveInArray(menuItemsClone, smartlyIndex, serenelyIndex);
            moveInArray(smartlySubMenuItems, smartlyOverViewHomeIndex, smartlyOverViewInCarIndex);

            setter(dataClone);
        }
    };

    query(window.HELIOS.selectors.application, modifyData);

    return <ShellComponent data={data} />;
};

const Shell = () => (
    <StorageProvider>
        <ApplicationProvider>
            <ShellRenderer />
        </ApplicationProvider>
    </StorageProvider>
);

export default Shell;
